import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";

import { paymentToastsAtom, type PaymentToast } from "@/core/state/paymentToasts";
import { useConversationLocks } from "@/panels/conversations/useConversationLocks";
import { useOpenFanTab } from "@/panels/conversations/useOpenFanTab";
import { VOLUME_DISCRET } from "@/core/pwa/paymentVolume";
import { lancerMiniVente } from "@/shell/fete/moteur";
import { VenteCelebration } from "./VenteCelebration";

/**
 * Feed de toasts de paiement — pendant VISUEL du son (cf usePaymentSound).
 *
 * Quand le cockpit est ouvert (desktop surtout), chaque paiement reçu affiche
 * une petite carte en bas à droite qui s'auto-efface. Alimenté par l'atome
 * `paymentToastsAtom` (poussé par usePaymentSound depuis le feed temps réel
 * Convex). Portal vers document.body pour ne pas être piégé par un ancêtre
 * `backdrop-filter`/`@container` du chrome acrylique.
 *
 * 💚 MA vente ne passe pas par la petite carte : elle prend le milieu de
 * l'écran (`VenteCelebration`). Les ventes des autres — et celles de Cyrano —
 * sont la MÊME fête en petit, en bas à droite (Kayla, 09-10-2026) : l'objet
 * de l'animation en miniature, ses éclats, et un son court et discret.
 */

const SYMBOLE: Record<string, string> = { EUR: "€", USD: "$", GBP: "£" };

/**
 * 🔒 Qui peut OUVRIR le fan depuis la carte d'une vente.
 *
 * Le fan d'une vente HUMAINE est à son vendeur : il l'a dans sa file, souvent
 * épinglé. Le 04-11-2026, KALOOO a cliqué la carte « Sabio a vendu 50 € » dix
 * secondes après le paiement : le verrou est passé chez lui, et Sabio ne
 * pouvait plus ouvrir son propre client. La garde « réservée par un autre » de
 * `useOpenFanTab` ne lit que la carte de la LISTE — absente chez celui qui
 * clique un toast — et le serveur du verrou ne connaît pas la pile.
 *
 * Donc : ma vente, une vente de Cyrano (le fan n'a pas de vendeur humain), ou
 * un superviseur (il ne verrouille jamais). La vente d'un collègue se fête,
 * elle ne s'ouvre pas.
 */
export function peutOuvrirLaVente(
  toast: Pick<PaymentToast, "conversationId" | "estAMoi" | "soldByAi">,
  superviseur: boolean,
): boolean {
  if (!toast.conversationId) return false;
  return toast.estAMoi || toast.soldByAi === true || superviseur;
}

export function PaymentToasts() {
  const [toasts, setToasts] = useAtom(paymentToastsAtom);
  // 🎯 Le geste unique d'ouverture du cockpit (verrou, heartbeat, onglet dans
  // le groupe du panneau Conversations) — jamais ré-implémenté ici.
  const openFan = useOpenFanTab();
  const { isBypass } = useConversationLocks();

  if (toasts.length === 0) return null;

  const dismiss = (id: string) =>
    setToasts((prev) => prev.filter((t) => t.id !== id));

  // La plus récente de MES ventes prend le milieu ; les autres, le coin.
  // `toasts` est trié du plus récent au plus ancien.
  const mienne = toasts.find((t) => t.estAMoi) ?? null;
  const autres = toasts.filter((t) => t !== mienne);

  const ouvrir = (toast: PaymentToast) =>
    toast.conversationId && peutOuvrirLaVente(toast, isBypass)
      ? () => {
          openFan({
            conversationId: toast.conversationId as string,
            title: toast.fanName ?? toast.creatorName ?? "Fan",
          });
          dismiss(toast.id);
        }
      : null;

  return createPortal(
    <>
    <VenteCelebration
      toast={mienne}
      onDismiss={() => mienne && dismiss(mienne.id)}
      onOpen={mienne ? ouvrir(mienne) : null}
    />
    <div className="pointer-events-none fixed bottom-4 right-4 z-[345] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
      {autres.map((toast) => (
        <PaymentToastCard
          key={toast.id}
          toast={toast}
          onDismiss={() => dismiss(toast.id)}
          onOpen={ouvrir(toast)}
        />
      ))}
    </div>
    </>,
    document.body,
  );
}

export function PaymentToastCard({
  toast,
  onDismiss,
  onOpen,
}: {
  toast: PaymentToast;
  onDismiss: () => void;
  /** Ouvre la conversation du fan ; `null` quand l'événement ne la porte pas. */
  onOpen: (() => void) | null;
}) {
  const { t } = useTranslation();
  const racine = useRef<HTMLDivElement>(null);
  const rappels = useRef({ onDismiss, onOpen });
  rappels.current = { onDismiss, onOpen };
  const ouvrable = onOpen !== null;

  useEffect(() => {
    if (!racine.current) return;
    // Qui a vendu : moi, Cyrano, ou un humain nommé.
    const vendeur = toast.soldByAi ? "Cyrano" : toast.soldByName;
    const qui = toast.estAMoi
      ? t("shell.paymentToast.mine", { defaultValue: "Tu as vendu !" })
      : vendeur
        ? t("shell.paymentToast.aVendu", { name: vendeur, defaultValue: "{{name}} a vendu" })
        : t("shell.paymentToast.title", { defaultValue: "Paiement reçu" });
    return lancerMiniVente(
      racine.current,
      {
        euros: toast.amountCents / 100,
        devise: SYMBOLE[toast.currency] ?? toast.currency,
        qui,
        fan: toast.fanName,
        createur: toast.creatorName,
      },
      { actif: toast.sonIci, volume: VOLUME_DISCRET },
      {
        fermer: t("common.close", { defaultValue: "Fermer" }),
        aQui: (name) => t("shell.venteCelebration.to", { name, defaultValue: "à {{name}}" }),
      },
      {
        onOuvrir: ouvrable ? () => rappels.current.onOpen?.() : null,
        onFermer: () => rappels.current.onDismiss(),
      },
    );
  }, [toast, t, ouvrable]);

  return (
    <div
      ref={racine}
      data-testid="payment-toast"
      data-ma-vente={toast.estAMoi ? "true" : "false"}
      className="pointer-events-auto"
      aria-label={ouvrable ? t("shell.paymentToast.open", { defaultValue: "Ouvrir la conversation" }) : undefined}
    />
  );
}
